import { AppBar, Box, IconButton, Stack, Toolbar } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';

import { PendingRegistrationAlert } from '@/layouts/dashboard/header/PendingRegistrationAlert';
import { RecusaNotaAvulsaAviso } from '@/pages/dashboard/Invoice/components/Avulsa/RecusaNotaAvulsaAviso';
import Iconify from '../../../components/iconify';
import Logo from '../../../components/logo';
import { useSettingsContext } from '../../../components/settings';
import { HEADER, NAV } from '../../../config-global';
import useOffSetTop from '../../../hooks/useOffSetTop';
import useResponsive from '../../../hooks/useResponsive';
import AccountPopover from './AccountPopover';
import BlackNovemberStrip from './BlackNovemberStrip';
import DarkModeSwitch from './DarkModeSwitch';
import NotificationsPopover from './NotificationsPopover';
import Searchbar from './Searchbar';

Header.propTypes = {
  onOpenNav: PropTypes.func,
};

export default function Header({ onOpenNav }) {
  const theme = useTheme();
  const { themeLayout } = useSettingsContext();
  const isNavHorizontal = themeLayout === 'horizontal';
  const isNavMini = themeLayout === 'mini';
  const isDesktop = useResponsive('up', 'lg');
  const isOffset = useOffSetTop(HEADER.H_DASHBOARD_DESKTOP) && !isNavHorizontal;
  // O header só tem os 92 px no desktop com a página no topo; rolando, no layout
  // horizontal ou abaixo do lg ele fica com 60–64 px, e a faixa cheia (~58 px)
  // encostaria nas bordas.
  const isTallHeader = isDesktop && !isOffset && !isNavHorizontal;

  const renderContent = (
    <>
      {isDesktop && isNavHorizontal && <Logo sx={{ mr: 2.5 }} />}

      {!isDesktop && (
        <IconButton
          onClick={onOpenNav}
          aria-label="Abrir menu"
          sx={{ mr: 1, color: 'text.primary' }}
        >
          <Iconify icon="eva:menu-2-fill" />
        </IconButton>
      )}

      <Searchbar />

      {/*
        O vão entre a busca e os ícones da conta não carregava nada. A faixa da
        campanha ocupa esse espaço e cresce no lugar do stack da direita, que
        antes tinha o `flexGrow` — sem a campanha no ar o componente devolve
        null, o Box vazio segue empurrando os ícones e o header fica como era.
      */}
      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          px: { xs: 0.5, lg: 2 },
        }}
      >
        <BlackNovemberStrip dense={!isTallHeader} />
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={{ xs: 0.5, sm: 1.5 }}
        sx={{ flexShrink: 0 }}
      >
        <DarkModeSwitch />
        <NotificationsPopover />
        <AccountPopover />
      </Stack>
    </>
  );

  return (
    <AppBar
      sx={{
        boxShadow: 'none',
        height: HEADER.H_MOBILE,
        zIndex: theme.zIndex.appBar + 1,
        backgroundColor: theme.palette.background.default,
        transition: theme.transitions.create(['height'], {
          duration: theme.transitions.duration.shorter,
        }),
        ...(isDesktop && {
          width: `calc(100% - ${NAV.W_DASHBOARD + 1}px)`,
          height: HEADER.H_DASHBOARD_DESKTOP,
          ...(isOffset && {
            height: HEADER.H_DASHBOARD_DESKTOP_OFFSET,
          }),
          ...(isNavHorizontal && {
            width: 1,
            bgcolor: 'background.default',
            height: HEADER.H_DASHBOARD_DESKTOP_OFFSET,
            borderBottom: `dashed 1px ${theme.palette.divider}`,
          }),
          ...(isNavMini && {
            width: `calc(100% - ${NAV.W_DASHBOARD_MINI + 1}px)`,
          }),
        }),
      }}
    >
      <PendingRegistrationAlert />
      <RecusaNotaAvulsaAviso />
      {/*
        `dense`: o Toolbar regular tem 64 px de mínimo e, no header de 60 px da
        página rolada, empurrava o conteúdo 4 px para baixo do centro. A altura
        segue a do header (`height: 1`), então nos estados de 64 e 92 px nada muda.
      */}
      <Toolbar variant="dense" sx={{ height: 1, px: { lg: 5 } }}>
        {renderContent}
      </Toolbar>
    </AppBar>
  );
}
